import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';

export default function AdvertiserForm({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    notes: '',
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchAdvertiser();
    }
  }, [id]);

  const fetchAdvertiser = async () => {
    try {
      const { data } = await api.get(`/api/advertisers/${id}`);
      setFormData({
        name: data.name || '',
        contact_name: data.contact_name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        website: data.website || '',
        notes: data.notes || '',
      });
    } catch (err) {
      console.error('[AdvertiserForm] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/advertisers/${id}`, formData);
      } else {
        await api.post('/api/advertisers', formData);
      }
      navigate('/advertising/advertisers');
    } catch (err) {
      console.error('[AdvertiserForm] Save failed:', err);
      if (err.response?.data?.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: 'Failed to save advertiser.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading advertiser...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {isEdit ? 'Edit Advertiser' : 'Create Advertiser'}
        </h1>
        <Link to="/advertising/advertisers" className="btn btn-secondary btn-sm">
          Back to Advertisers
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`input ${errors.name ? 'border-error-500' : ''}`}
              placeholder="Company / brand name"
            />
            {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Contact Person</label>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleChange}
              className="input"
              placeholder="Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`input ${errors.email ? 'border-error-500' : ''}`}
              placeholder="contact@example.com"
            />
            {errors.email && <p className="text-xs text-error-400 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="input"
              placeholder="+1 555 1234"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-dark-300 mb-1">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="input min-h-[60px]"
              placeholder="Street address..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Website</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="input"
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Notes</label>
            <input
              type="text"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="input"
              placeholder="Internal notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
          <Link to="/advertising/advertisers" className="btn btn-secondary">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Saving...' : isEdit ? 'Update Advertiser' : 'Create Advertiser'}
          </button>
        </div>
      </form>
    </div>
  );
}
